/**
 * Helpers for AI answer flags (IsAI* / Is*Ai / IsAIAnswer / IsAI).
 */

export function isEmptyAnswerValue(value: unknown): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (typeof value === 'number') {
        return Number.isNaN(value);
    }
    if (typeof value === 'boolean') {
        return false;
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        if (Object.prototype.hasOwnProperty.call(record, 'Name')) {
            return isEmptyAnswerValue(record.Name);
        }
        // Height-style objects: empty when all measurement parts are empty
        if (Object.prototype.hasOwnProperty.call(record, 'Feet') || Object.prototype.hasOwnProperty.call(record, 'Inch')) {
            return isEmptyAnswerValue(record.Feet) && isEmptyAnswerValue(record.Inch);
        }
        return false;
    }
    return false;
}

function isAiFlagKey(key: string): boolean {
    if (key === 'IsAIAnswer' || key === 'IsAI') {
        return true;
    }
    if (key.startsWith('IsAI') && key.length > 4) {
        return true;
    }
    if (key.startsWith('Is') && key.endsWith('Ai') && key.length > 4) {
        return true;
    }
    return false;
}

function getPairedFieldName(flagKey: string): string | null {
    if (flagKey === 'IsAIAnswer' || flagKey === 'IsAI') {
        return null;
    }
    if (flagKey.startsWith('IsAI')) {
        return flagKey.slice(4);
    }
    if (flagKey.startsWith('Is') && flagKey.endsWith('Ai')) {
        return flagKey.slice(2, -2);
    }
    return null;
}

/** Known answer-field aliases when AI flag naming does not match the stored property. */
const PAIRED_FIELD_ALIASES: Record<string, string[]> = {
    IsAIAverageTobaccoUsagePerDay: ['AverageToboccoUsagePerDay', 'AverageTobaccoUsagePerDay'],
};

function getPairedFieldCandidates(flagKey: string): string[] {
    if (PAIRED_FIELD_ALIASES[flagKey]) {
        return PAIRED_FIELD_ALIASES[flagKey];
    }
    const primary = getPairedFieldName(flagKey);
    if (!primary) {
        return [];
    }
    const candidates = [primary];
    // IsHDLCholestrolOutOfRangeAi -> IsHDLCholestrolOutOfRange, HDLCholestrol
    if (flagKey.endsWith('OutOfRangeAi') && flagKey.startsWith('Is')) {
        candidates.push(`Is${primary}`);
        if (primary.endsWith('OutOfRange')) {
            candidates.push(primary.slice(0, -'OutOfRange'.length));
        }
    }
    return candidates;
}

function objectHasAiAnswerContent(obj: Record<string, any>): boolean {
    // Physical Health questions: Description is question text, not an answer.
    const isQuestionShape =
        Array.isArray(obj.Options) || Object.prototype.hasOwnProperty.call(obj, 'SelectionType');
    if (isQuestionShape) {
        if (!isEmptyAnswerValue(obj.AnswerText) || !isEmptyAnswerValue(obj.Date)) {
            return true;
        }
        if (Array.isArray(obj.Options) && obj.Options.some((option: any) => option?.IsSelected)) {
            return true;
        }
        return false;
    }

    // Mini Mental / Factors & Risks style items (Name/Label are titles, not answers)
    const answerKeys = [
        'AnswerText',
        'Date',
        'Type',
        'Types',
        'Description',
        'MemberOption',
        'ProviderOption',
        'MemberReports',
        'ProviderAssessed',
        'TextAnswer',
    ];
    for (const key of answerKeys) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) {
            continue;
        }
        const value = obj[key];
        // Member/Provider option enums use 0 as unanswered
        if ((key === 'MemberOption' || key === 'ProviderOption') && (value === 0 || value === '0')) {
            continue;
        }
        if (!isEmptyAnswerValue(value)) {
            return true;
        }
    }
    if (Array.isArray(obj.Types) && obj.Types.length > 0) {
        return true;
    }
    return false;
}

/** True when an assessment item/question still has a non-empty answer suitable for showing the AI icon. */
export function hasAiAnswerContent(item: Record<string, any>): boolean {
    return objectHasAiAnswerContent(item);
}

function walkAndSanitizeAiFlags(obj: any): void {
    if (!obj || typeof obj !== 'object') {
        return;
    }
    if (Array.isArray(obj)) {
        obj.forEach(walkAndSanitizeAiFlags);
        return;
    }

    for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (isAiFlagKey(key) && value === true) {
            if (key === 'IsAIAnswer' || key === 'IsAI') {
                if (!objectHasAiAnswerContent(obj)) {
                    obj[key] = false;
                }
            } else {
                const pairedFields = getPairedFieldCandidates(key);
                const shouldClear = pairedFields.some(
                    (pairedField) => Object.prototype.hasOwnProperty.call(obj, pairedField) && isEmptyAnswerValue(obj[pairedField])
                );
                if (shouldClear) {
                    obj[key] = false;
                }
            }
        } else if (value && typeof value === 'object') {
            walkAndSanitizeAiFlags(value);
        }
    }
}

/**
 * Clears AI flags whose paired answer is empty/null. Walks nested objects and arrays.
 */
export function sanitizeAiFlagsForEmptyAnswers<T>(data: T): T {
    if (!data || typeof data !== 'object') {
        return data;
    }
    walkAndSanitizeAiFlags(data);
    return data;
}

function walkAndClearAiFlags(obj: any): void {
    if (!obj || typeof obj !== 'object') {
        return;
    }
    if (Array.isArray(obj)) {
        obj.forEach(walkAndClearAiFlags);
        return;
    }

    for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (isAiFlagKey(key)) {
            if (value === true) {
                obj[key] = false;
            }
        } else if (value && typeof value === 'object') {
            walkAndClearAiFlags(value);
        }
    }
}

/**
 * Clears every AI flag regardless of answer content. Walks nested objects and arrays.
 * AI flags are only valid on the assessment Heidi processing created, so data carried
 * forward from a prior assessment must never show them.
 */
export function clearAllAiFlags<T>(data: T): T {
    if (!data || typeof data !== 'object') {
        return data;
    }
    walkAndClearAiFlags(data);
    return data;
}

/**
 * Prepares a prior member assessment for use on a new draft: clears Id (and optionally Status)
 * and drops all AI flags.
 */
export function preparePriorAssessmentForNewDraft<T extends Record<string, any>>(
    data: T,
    options?: { clearStatus?: boolean }
): T {
    if (!data) {
        return data;
    }
    (data as any).Id = null;
    if (options?.clearStatus) {
        if (Object.prototype.hasOwnProperty.call(data, 'Status')) {
            (data as any).Status = 0;
        }
        if (Object.prototype.hasOwnProperty.call(data, 'AssessmentStatus')) {
            (data as any).AssessmentStatus = 0;
        }
        if (Object.prototype.hasOwnProperty.call(data, 'IsAssessmentCompleted')) {
            (data as any).IsAssessmentCompleted = false;
        }
    }
    return clearAllAiFlags(data);
}

/**
 * Clears AI flags that correspond to a manually edited field.
 * Does not clear when the changed property itself is an AI flag.
 */
export function clearPairedAiFlags<T extends Record<string, any>>(data: T, propsName: string, flagNames?: string[]): T {
    if (propsName.startsWith('Is') && (propsName.endsWith('Ai') || propsName.endsWith('AI') || propsName === 'IsAIAnswer' || propsName === 'IsAI')) {
        return data;
    }

    const flagsToClear = flagNames ?? [`Is${propsName}Ai`, `IsAI${propsName}`];
    const mutableData = data as Record<string, any>;
    for (const flagName of flagsToClear) {
        if (Object.prototype.hasOwnProperty.call(mutableData, flagName)) {
            mutableData[flagName] = false;
        }
    }
    return data;
}
